import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  type GridColDef,
  type GridFilterModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import { resources } from '../../core/services/resources';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { DetailsInfoTooltip } from '../../shared/components/DetailsInfoTooltip';
import { StatusChip } from '../../shared/components/StatusChip';
import { formatDate } from '../../shared/utils/format';
import { extractErrorMessage } from '../../core/services/api';
import type {
  PromptResponseStatus,
  ReportInstancePrompt,
} from '../../core/types/data-models';

// Status buckets for the "prompt responses" card, in lifecycle order.
const RESPONSE_STATUSES: PromptResponseStatus[] = ['PENDING', 'IN_PROGRESS', 'DONE', 'FAILED'];

// The prompts table sorts/filters server-side, so only offer what the backend
// supports (report-instance-prompt's allowedSorts / allowedFilters). AI Engine
// and Geo Location are filterable via dropdown (they map to the numeric
// aiModelId / geoLocationId under the hood) but not sortable — the backend
// whitelists those ids for filtering only. Response status lives on the joined
// promptResponse; the backend translates the `responseStatus` filter/sort to a
// relation query, so it's both filterable and sortable here.
const SORTABLE_FIELDS = new Set(['id', 'promptId', 'importance', 'language', 'intentionType', 'responseStatus']);
// Fields translated straight through as exact-match / `in`. `aiModelId` is
// handled separately because its dropdown holds engine names, not ids.
const DIRECT_FILTER_FIELDS = new Set([
  'id',
  'promptId',
  'importance',
  'language',
  'intentionType',
  'geoLocationId',
  'responseStatus',
]);

// The backend does exact-match (or comma → `in`) only — no contains / ranges.
// Restrict each column's filter operators to the ones that map cleanly.
const NUMERIC_OPS = getGridNumericOperators().filter((o) => o.value === '=' || o.value === 'isAnyOf');
const STRING_OPS = getGridStringOperators().filter((o) => o.value === 'equals' || o.value === 'isAnyOf');
const SELECT_OPS = getGridSingleSelectOperators().filter((o) => o.value === 'is' || o.value === 'isAnyOf');

// Default order when the user hasn't picked a column sort: a virtual sort the
// backend computes in-app — response status by priority (IN_PROGRESS first),
// then executeStartTime ascending.
const DEFAULT_SORT = 'responseStatusPriority';

/** MUI sort model → backend `sort` param (`-field` for desc); priority default. */
function sortModelToParam(model: GridSortModel): string {
  const parts = model
    .filter((s) => s.sort && SORTABLE_FIELDS.has(s.field))
    .map((s) => (s.sort === 'desc' ? `-${s.field}` : s.field));
  return parts.length > 0 ? parts.join(',') : DEFAULT_SORT;
}

/**
 * Read-only detail view for a single report instance, reached from the
 * Reports Monitoring list via "Open details". Reads run cross-tenant
 * (`X-Tenant-Id: *`) so the page works regardless of the active customer —
 * the backend still scopes every query to the tenants the user can access.
 */
export function ReportInstanceDetail() {
  const { id } = useParams();
  const instanceId = Number(id);
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
  // Empty → the backend's default priority sort (see sortModelToParam). Picking
  // a column header overrides it.
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  // Row double-clicked in the prompts table → opens the right-hand detail panel.
  const [selectedPrompt, setSelectedPrompt] = useState<ReportInstancePrompt | null>(null);

  const sortParam = useMemo(() => sortModelToParam(sortModel), [sortModel]);

  // Sort/filter changes can invalidate the current page index — go back to
  // the first page so the user isn't stranded on an out-of-range page.
  const handleSortModelChange = (m: GridSortModel) => {
    setSortModel(m);
    setPagination((p) => ({ ...p, page: 0 }));
  };
  const handleFilterModelChange = (m: GridFilterModel) => {
    setFilterModel(m);
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const instance = useQuery({
    queryKey: ['report-instance', instanceId],
    queryFn: () => resources.reportInstances.get(instanceId, undefined, { tenantId: '*' }),
    enabled: Number.isFinite(instanceId),
  });

  const stats = useQuery({
    queryKey: ['report-instance', instanceId, 'stats'],
    queryFn: () => resources.reportInstanceStats(instanceId, { tenantId: '*' }),
    enabled: Number.isFinite(instanceId),
  });

  const template = useQuery({
    queryKey: ['report-template', instance.data?.reportTemplateId],
    queryFn: () =>
      resources.reportTemplates.get(instance.data!.reportTemplateId, undefined, { tenantId: '*' }),
    enabled: instance.data?.reportTemplateId != null,
  });

  // Global catalogs (tenantless) → id lookups for the AI Engine / Geo
  // Location columns and their dropdown filters. Cached across the session.
  const aiModels = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });
  const geoLocations = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  // categoryId / personaId point at the LOB-scoped LineOfBizCategory /
  // LineOfBizPersona (not the global catalogs). These are tenant-scoped, so
  // load them cross-tenant like the rest of the page.
  const lineOfBizCategories = useQuery({
    queryKey: ['line-of-biz-categories', 'select'],
    queryFn: () =>
      resources.lineOfBizCategories.list({ pageSize: 500, sort: 'name' }, { tenantId: '*' }),
  });
  const lineOfBizPersonas = useQuery({
    queryKey: ['line-of-biz-personas', 'select'],
    queryFn: () =>
      resources.lineOfBizPersonas.list({ pageSize: 500, sort: 'name' }, { tenantId: '*' }),
  });
  const stages = useQuery({
    queryKey: ['prompt-funnel-stages', 'select'],
    queryFn: () => resources.promptFunnelStages.list({ pageSize: 500, sort: 'name' }),
  });
  const aiEngineById = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of aiModels.data?.data ?? []) map.set(m.id, m.aiEngine);
    return map;
  }, [aiModels.data]);
  const geoNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const g of geoLocations.data?.data ?? []) map.set(g.id, g.name || g.code);
    return map;
  }, [geoLocations.data]);
  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const c of lineOfBizCategories.data?.data ?? []) map.set(c.id, c.name);
    return map;
  }, [lineOfBizCategories.data]);
  const personaNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of lineOfBizPersonas.data?.data ?? []) map.set(p.id, p.name);
    return map;
  }, [lineOfBizPersonas.data]);
  const stageNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stages.data?.data ?? []) map.set(s.id, s.name);
    return map;
  }, [stages.data]);

  // One AI engine spans several models, so the AI Engine dropdown lists
  // distinct engines and each selection expands to that engine's model ids
  // (the backend filters by the numeric aiModelId via `in`).
  const engineToModelIds = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const m of aiModels.data?.data ?? []) {
      const list = map.get(m.aiEngine) ?? [];
      list.push(m.id);
      map.set(m.aiEngine, list);
    }
    return map;
  }, [aiModels.data]);
  const engineOptions = useMemo(() => [...engineToModelIds.keys()].sort(), [engineToModelIds]);
  const geoOptions = useMemo(
    () => (geoLocations.data?.data ?? []).map((g) => ({ value: g.id, label: g.name || g.code })),
    [geoLocations.data],
  );

  // MUI filter model → backend `filter[field]=value` params. Exact-match /
  // `in` only. The AI Engine column carries engine names, expanded here to
  // the matching aiModelId set; every other field passes straight through.
  const userFilters = useMemo(() => {
    const out: Record<string, string | number> = {};
    for (const item of filterModel.items) {
      const { field, operator, value } = item;
      if (field === 'aiModelId') {
        const engines =
          operator === 'isAnyOf'
            ? Array.isArray(value)
              ? value
              : []
            : value !== undefined && value !== null && value !== ''
              ? [value]
              : [];
        const ids = engines.flatMap((e) => engineToModelIds.get(String(e)) ?? []);
        if (ids.length > 0) out.aiModelId = ids.join(',');
        continue;
      }
      if (!DIRECT_FILTER_FIELDS.has(field)) continue;
      if (operator === 'isAnyOf') {
        if (Array.isArray(value) && value.length > 0) out[field] = value.join(',');
      } else if (value !== undefined && value !== null && value !== '') {
        // 'is' (singleSelect), 'equals' (string), '=' (number) — all exact match.
        out[field] = String(value);
      }
    }
    return out;
  }, [filterModel, engineToModelIds]);

  const prompts = useQuery({
    queryKey: ['report-instance-prompts', instanceId, pagination, sortParam, userFilters],
    queryFn: () =>
      resources.reportInstancePrompts.list(
        {
          page: pagination.page + 1,
          pageSize: pagination.pageSize,
          sort: sortParam,
          // reportInstanceId always scopes the list; user filters are ANDed on.
          filter: { reportInstanceId: instanceId, ...userFilters },
          include: ['promptResponse'],
        },
        { tenantId: '*' },
      ),
    enabled: Number.isFinite(instanceId),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef<ReportInstancePrompt>[] = [
    { field: 'id', headerName: 'ID', width: 90, type: 'number', filterOperators: NUMERIC_OPS },
    {
      field: 'promptId',
      headerName: 'Prompt',
      width: 140,
      type: 'number',
      filterOperators: NUMERIC_OPS,
      // Prompt id + an info icon that lazily loads the prompt text on hover.
      renderCell: (p) => (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ height: '100%' }}>
          <span>{p.value as number}</span>
          <PromptTextTooltip promptId={(p.row as ReportInstancePrompt).promptId} />
        </Stack>
      ),
    },
    {
      field: 'aiModelId',
      headerName: 'AI Engine',
      width: 150,
      // Filter by engine (dropdown → expanded to aiModelId `in` server-side);
      // not sortable — the backend whitelists aiModelId for filtering only.
      // `valueGetter` maps the row's aiModelId to its engine so the cell and
      // the singleSelect filter share one set of option values.
      type: 'singleSelect',
      valueOptions: engineOptions,
      valueGetter: (value) => (value == null ? null : (aiEngineById.get(value as number) ?? null)),
      filterOperators: SELECT_OPS,
      sortable: false,
      renderCell: (p) => (p.value as string | null) ?? '—',
    },
    {
      field: 'geoLocationId',
      headerName: 'Geo Location',
      width: 160,
      // Filter by location (dropdown → geoLocationId server-side); not
      // sortable (backend whitelists geoLocationId for filtering only).
      type: 'singleSelect',
      valueOptions: geoOptions,
      filterOperators: SELECT_OPS,
      sortable: false,
      renderCell: (p) => {
        const id = p.value as number | null;
        if (id == null) return '—';
        return geoNameById.get(id) ?? `#${id}`;
      },
    },
    {
      field: 'importance',
      headerName: 'Importance',
      width: 130,
      type: 'singleSelect',
      valueOptions: ['LOW', 'MEDIUM', 'HIGH'],
      filterOperators: SELECT_OPS,
      renderCell: (p) => <StatusChip value={p.value as string} />,
    },
    { field: 'language', headerName: 'Language', width: 110, filterOperators: STRING_OPS },
    {
      field: 'intentionType',
      headerName: 'Intention',
      width: 150,
      type: 'singleSelect',
      valueOptions: ['BRANDED', 'UNBRANDED', 'INFORMATIONAL', 'COMPARISON'],
      filterOperators: SELECT_OPS,
    },
    {
      field: 'responseStatus',
      headerName: 'Response status',
      width: 170,
      // Status of the joined promptResponse. Filter/sort run server-side via
      // the `responseStatus` param, which the backend translates to a relation
      // query. `valueGetter` reads the status off the included row so the cell
      // and the singleSelect filter share one set of option values.
      type: 'singleSelect',
      valueOptions: ['PENDING', 'IN_PROGRESS', 'DONE', 'FAILED'],
      filterOperators: SELECT_OPS,
      valueGetter: (_value, row) => (row as ReportInstancePrompt).promptResponse?.status ?? null,
      // Blank when the prompt has no response yet (worker link step hasn't run).
      renderCell: (p) => {
        const status = (p.row as ReportInstancePrompt).promptResponse?.status;
        return status ? <StatusChip value={status} /> : '—';
      },
    },
    {
      field: 'executeStartTime',
      headerName: 'Started',
      width: 180,
      // Execution timestamps live on the joined promptResponse. The backend
      // whitelists neither for sort nor filter, so these are display-only.
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        formatDate((p.row as ReportInstancePrompt).promptResponse?.executeStartTime) || '—',
    },
    {
      field: 'executeEndTime',
      headerName: 'Finished',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (p) =>
        formatDate((p.row as ReportInstancePrompt).promptResponse?.executeEndTime) || '—',
    },
    {
      field: 'retryCount',
      headerName: 'Retries',
      width: 100,
      type: 'number',
      // On the joined promptResponse; not in the backend sort/filter whitelist.
      sortable: false,
      filterable: false,
      renderCell: (p) => (p.row as ReportInstancePrompt).promptResponse?.retryCount ?? '—',
    },
  ];

  const title = instance.data ? `Report instance #${instance.data.id}` : `Report instance #${instanceId}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{title}</span>
            {instance.data && (
              <DetailsInfoTooltip
                rows={[
                  { label: 'Template', value: template.data?.name ?? `#${instance.data.reportTemplateId}` },
                  { label: 'Status', value: <StatusChip value={instance.data.status} /> },
                  { label: 'Priority', value: <StatusChip value={instance.data.priority} /> },
                  {
                    label: 'Relevant period',
                    value:
                      instance.data.relevantPeriod == null
                        ? '—'
                        : `${instance.data.relevantPeriod} day${instance.data.relevantPeriod === 1 ? '' : 's'}`,
                  },
                  { label: 'Created', value: formatDate(instance.data.createdAt) },
                  {
                    label: 'Generated',
                    value: instance.data.generateDate ? formatDate(instance.data.generateDate) : '—',
                  },
                ]}
              />
            )}
          </Stack>
        }
        actions={<Button onClick={() => navigate(-1)}>Back</Button>}
      />

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ flexWrap: 'wrap', '& > *': { flex: '1 1 240px' } }}
        >
          <StatCard title="Prompts" loading={stats.isLoading} error={stats.isError}>
            <BigNumber value={stats.data?.promptCount} />
          </StatCard>

          <StatCard title="Prompt responses" loading={stats.isLoading} error={stats.isError}>
            <BigNumber value={stats.data?.responseCount} />
            {stats.data && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                {RESPONSE_STATUSES.filter((s) => (stats.data!.responsesByStatus[s] ?? 0) > 0).map(
                  (s) => (
                    <Stack key={s} direction="row" alignItems="center" spacing={0.5}>
                      <StatusChip value={s} />
                      <Typography variant="body2" fontWeight={600}>
                        {stats.data!.responsesByStatus[s]}
                      </Typography>
                    </Stack>
                  ),
                )}
                {stats.data.responseCount === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No responses yet
                  </Typography>
                )}
              </Stack>
            )}
          </StatCard>

          <StatCard title="Sources" loading={stats.isLoading} error={stats.isError}>
            <BigNumber value={stats.data?.sourceCount} />
          </StatCard>
        </Stack>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Report instance prompts
          </Typography>
          <DataTable<ReportInstancePrompt>
            rows={prompts.data?.data ?? []}
            columns={columns}
            loading={prompts.isFetching}
            rowCount={prompts.data?.page.total ?? 0}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            onRowDoubleClick={(params) => setSelectedPrompt(params.row as ReportInstancePrompt)}
            errorMessage={prompts.isError ? extractErrorMessage(prompts.error) : undefined}
            hideImport
            hideQuickFilter
          />
        </Box>
      </Stack>

      <PromptDetailPanel
        prompt={selectedPrompt}
        onClose={() => setSelectedPrompt(null)}
        aiEngineById={aiEngineById}
        geoNameById={geoNameById}
        categoryNameById={categoryNameById}
        personaNameById={personaNameById}
        stageNameById={stageNameById}
      />
    </Box>
  );
}

function StatCard({
  title,
  loading,
  error,
  children,
}: {
  title: string;
  loading?: boolean;
  error?: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <Typography variant="caption" color="text.secondary" display="block">
          {title}
        </Typography>
        {error ? (
          <Typography variant="body2" color="error">
            Failed to load
          </Typography>
        ) : loading ? (
          <Typography variant="h4" color="text.secondary">
            …
          </Typography>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function BigNumber({ value }: { value: number | undefined }) {
  return (
    <Typography variant="h4" fontWeight={700}>
      {value?.toLocaleString() ?? '—'}
    </Typography>
  );
}

/**
 * Info icon that reveals a prompt's text on hover. The prompt is fetched
 * lazily — the query only runs the first time the tooltip opens, then stays
 * cached — so listing the table doesn't fan out a request per row.
 */
function PromptTextTooltip({ promptId }: { promptId: number }) {
  const [open, setOpen] = useState(false);
  const prompt = useQuery({
    queryKey: ['prompt', promptId, 'text'],
    queryFn: () => resources.prompts.get(promptId, undefined, { tenantId: '*' }),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const text = prompt.isLoading
    ? 'Loading…'
    : prompt.isError
      ? 'Failed to load prompt'
      : (prompt.data?.prompt ?? '—');

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-wrap', maxWidth: 360, py: 0.5 }}>{text}</Box>}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    >
      <IconButton size="small" aria-label={`show prompt ${promptId} text`} sx={{ color: 'text.secondary' }}>
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Right-hand drawer showing the full detail of one report-instance prompt,
 * opened by double-clicking its table row. The prompt/instance/response data
 * mostly comes from the already-loaded row (the list includes
 * `promptResponse`); only the prompt's text is fetched lazily — sharing the
 * `['prompt', id, 'text']` cache with the table's hover tooltip. Engine and
 * geo ids are resolved via the maps the table already built.
 */
function PromptDetailPanel({
  prompt,
  onClose,
  aiEngineById,
  geoNameById,
  categoryNameById,
  personaNameById,
  stageNameById,
}: {
  prompt: ReportInstancePrompt | null;
  onClose: () => void;
  aiEngineById: Map<number, string>;
  geoNameById: Map<number, string>;
  categoryNameById: Map<number, string>;
  personaNameById: Map<number, string>;
  stageNameById: Map<number, string>;
}) {
  const open = prompt != null;
  // Retain the last selection so the content doesn't blank out mid-close animation.
  const shownRef = useRef<ReportInstancePrompt | null>(null);
  if (prompt) shownRef.current = prompt;
  const shown = prompt ?? shownRef.current;

  const promptQuery = useQuery({
    queryKey: ['prompt', shown?.promptId, 'text'],
    queryFn: () => resources.prompts.get(shown!.promptId, undefined, { tenantId: '*' }),
    enabled: open && shown != null,
    staleTime: 5 * 60 * 1000,
  });

  const promptText = promptQuery.isLoading
    ? 'Loading…'
    : promptQuery.isError
      ? 'Failed to load prompt'
      : (promptQuery.data?.prompt ?? '—');

  const response = shown?.promptResponse;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' } }}
    >
      {shown && (
        <>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ p: 2, pb: 1.5 }}
          >
            <Box>
              <Typography variant="h6">Prompt details</Typography>
              <Typography variant="caption" color="text.secondary">
                Instance prompt #{shown.id}
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label="close details panel" edge="end">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Stack spacing={3}>
              <Section title="Prompt">
                <Field label="Prompt ID" value={shown.promptId} />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Prompt text
                  </Typography>
                  <TextBlock>{promptText}</TextBlock>
                </Box>
              </Section>

              <Section title="Assignment">
                <Field label="AI engine" value={resolveCatalog(shown.aiModelId, aiEngineById)} />
                <Field label="Geo location" value={resolveCatalog(shown.geoLocationId, geoNameById)} />
                <Field label="Category" value={resolveCatalog(shown.categoryId, categoryNameById)} />
                <Field label="Persona" value={resolveCatalog(shown.personaId, personaNameById)} />
                <Field label="Stage" value={resolveCatalog(shown.stageId, stageNameById)} />
                <Field label="Brand ID" value={shown.brandId} />
                <Field label="Importance" value={<StatusChip value={shown.importance} />} />
                <Field label="Language" value={shown.language} />
                <Field label="Intention" value={shown.intentionType} />
                <Field label="Week identifier" value={shown.weekIdentifier} />
                <Field label="Response ID" value={shown.promptResponseId} />
                <Field label="Created" value={formatDate(shown.createdAt)} />
              </Section>

              <Section title="Response">
                {response ? (
                  <>
                    <Field label="Status" value={<StatusChip value={response.status} />} />
                    <Field label="Retry count" value={response.retryCount} />
                    <Field label="Execution time" value={formatMs(response.totalExecutionTimeMs)} />
                    <Field label="Started" value={formatDate(response.executeStartTime) || '—'} />
                    <Field label="Finished" value={formatDate(response.executeEndTime) || '—'} />
                    <Field label="Prompt tokens" value={response.promptTokens} />
                    <Field label="Completion tokens" value={response.promptCompletionTokens} />
                    <Field label="Worker ID" value={response.workerId} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Response text
                      </Typography>
                      <TextBlock>{response.response ?? '—'}</TextBlock>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No response generated yet.
                  </Typography>
                )}
              </Section>
            </Stack>
          </Box>
        </>
      )}
    </Drawer>
  );
}

/** `id → name` catalog lookup for the panel; falls back to `#id`, or '—' when unset. */
function resolveCatalog(id: number | null, byId: Map<number, string>): string {
  if (id == null) return '—';
  return `${byId.get(id) ?? 'Unknown'} (#${id})`;
}

/** Milliseconds as a compact human string (`820 ms`, `1.24 s`). */
function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`;
}

/** Section wrapper with an overline heading in the detail panel. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {title}
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {children}
      </Stack>
    </Box>
  );
}

/** Label/value row in the detail panel; blank values render as an em dash. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="baseline">
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        component="div"
        sx={{ textAlign: 'right', wordBreak: 'break-word' }}
      >
        {value ?? '—'}
      </Typography>
    </Stack>
  );
}

/** Scrollable, wrapped, monospace text block for prompt / response bodies. */
function TextBlock({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        maxHeight: 280,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily: 'monospace',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  );
}
